<script>

export default {
  methods: {
    redirect(route) {
      if (this.language === "en")
        // window.location.href = `/${this.language}/${route}`;
        return `/${this.language}/${route}`;
      else return `/${route}`;
    },
  },
  computed: {
    language() {
      return this.$i18n.locale;
    },
    appleLink() {
      return this.language === "en"
        ? "https://apps.apple.com/us/app/salamis-bay-conti-resort-spa/id6477930423"
        : `https://apps.apple.com/tr/app/salamis-bay-conti-resort-spa/id6477930423?l=${this.language}`;
    },
  },
  setup() {
    const store = generalStore();
    const { locale } = useI18n();

    // Same key as pages/index.vue's own useFetch — on the homepage, that
    // SSR call already populates this payload entry, so this client-only
    // read reuses it instead of re-fetching. On every other page (where
    // nothing else fetches this), it's still just the one request Footer
    // always made, now going through Nuxt's asyncData cache instead of a
    // raw, un-keyed $fetch. `server: false` keeps this exactly as
    // client-only as the old mounted()-based fetch was, so it can't add
    // a new blocking call to every page's SSR/TTFB.
    const { data: covers } = useAsyncData(
      'index-carousel-covers',
      () => $fetch('https://api2.salamisbayconti.com/api/general/carousel'),
      { server: false }
    );
    watch(covers, (val) => { if (val) store.setCovers(val); }, { immediate: true });

    const { data: variables } = useAsyncData(
      'footer-variables',
      () => $fetch(`https://api2.salamisbayconti.com/api/general/${locale.value}/variable`),
      { server: false, watch: [locale] }
    );
    watch(variables, (val) => { if (val) store.setVariables(val); }, { immediate: true });

    const iosLoaded = ref(false);
    const googleLoaded = ref(false);
    const qrLoaded = ref(false);

    return { iosLoaded, googleLoaded, qrLoaded };
  },
};
</script>

<template>
  <section class="footer-section">
    <div class="footer w-full text-white">
      <div>
        <div class="container">
            <div class="row pad align-items-start">
              <ul class="col-12 col-lg-5 d-flex flex-column align-items-start">
                <li>
                  <NuxtLink
                    :to="language === 'en' ? '/en' : '/'"
                    class="d-flex align-items-center link-body-emphasis text-decoration-none"
                  >
                    <NuxtImg
                      format="webp"
                      class="logo h-auto"
                      src="https://api2.salamisbayconti.com/storage/salamislogo.png"
                      alt="Salamis Bay Conti Logo"
                      loading="lazy"
                      width="200"
                      height="91"
                      sizes="200px"
                    />
                  </NuxtLink>
                </li>
                <li>
                  <p class="app-cta">{{ $t("app_cta") }}</p>
                </li>
                <li class="app-review-row">
                  <div class="d-flex align-items-center flex-wrap gap-2">
                    <div class="d-flex flex-column gap-1">
                      <a
                        :href="appleLink"
                        class="text-decoration-none"
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        <span class="badge-frame">
                          <span v-if="!iosLoaded" class="footer-skel" aria-hidden="true"></span>
                          <NuxtImg
                            format="webp"
                            class="icon h-auto"
                            src="https://api2.salamisbayconti.com/storage/ios.png"
                            alt="Download on App Store"
                            loading="lazy"
                            width="140"
                            height="44"
                            sizes="140px"
                            @load="iosLoaded = true"
                          />
                        </span>
                      </a>
                      <a
                        href="https://play.google.com/store/apps/details?id=com.salamisbayconti.hotelapp"
                        class="text-decoration-none"
                        rel="nofollow noopener noreferrer"
                        target="_blank"
                      >
                        <span class="badge-frame">
                          <span v-if="!googleLoaded" class="footer-skel" aria-hidden="true"></span>
                          <NuxtImg
                            format="webp"
                            class="icon h-auto"
                            src="https://api2.salamisbayconti.com/storage/google.png"
                            alt="Get it on App Store"
                            loading="lazy"
                            width="140"
                            height="44"
                            sizes="140px"
                            @load="googleLoaded = true"
                          />
                        </span>
                      </a>
                    </div>
                    <span class="qr-frame">
                      <span v-if="!qrLoaded" class="footer-skel" aria-hidden="true"></span>
                      <NuxtImg
                        format="webp"
                        class="qr h-auto p-0 rounded"
                        src="https://api2.salamisbayconti.com/storage/QR_Salamis.png"
                        alt="QR Code"
                        loading="lazy"
                        width="80"
                        height="80"
                        sizes="80px"
                        @load="qrLoaded = true"
                      />
                    </span>
                  </div>
                </li>
              </ul>
              <ul class="col-6 col-md-4 col-lg-2">
                <li class="nav-item">
                  <NuxtLink :to="redirect('about')" class="nav-link">{{
                    $t("about")
                  }}</NuxtLink>
                </li>
                <li class="nav-item">
                  <NuxtLink :to="redirect('rooms')" class="nav-link">
                    {{ $t("Rooms") }}
                  </NuxtLink>
                </li>
                <li class="nav-item">
                  <NuxtLink
                    class="nav-link header-text pointer"
                    :to="redirect('flavors')"
                  >
                    {{ $t("Flavors") }}
                  </NuxtLink>
                </li>
                
                <li class="nav-item">
                  <NuxtLink
                    :to="redirect('events')"
                    class="nav-link header-text pointer"
                  >
                    {{ $t("Events") }}
                  </NuxtLink>
                </li>
                <li class="nav-item">
                  <NuxtLink
                    :to="redirect('spa')"
                    class="nav-link header-text pointer"
                  >
                    {{ $t("Spa") }}
                  </NuxtLink>
                </li>
              </ul>
              <ul class="col-6 col-md-4 col-lg-2">
                <li class="nav-item">
                  <NuxtLink :to="redirect('contact')" class="nav-link">{{
                    $t("Contact")
                  }}</NuxtLink>
                </li><li class="nav-item">
                  <NuxtLink :to="redirect('concepts')" class="nav-link">{{
                    $t("concepts")
                  }}</NuxtLink>
                </li>
                <li class="nav-item">
                  <NuxtLink :to="redirect('career')" class="nav-link">{{
                    $t("career")
                  }}</NuxtLink>
                </li>
                <li class="nav-item">
                  <NuxtLink :to="redirect('faqs')" class="nav-link">{{ $i18n.locale === 'tr' ? 'SSS' : 'FAQ' }}</NuxtLink>
                </li>
                <li class="nav-item">
                  <NuxtLink :to="redirect('gallery')" class="nav-link">{{
                    $t("gallery")
                  }}</NuxtLink>
                </li>
              </ul>
              <ul class="col-12 col-md-4 col-lg-3">
                <li class="itm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M12 11.5A2.5 2.5 0 0 1 9.5 9A2.5 2.5 0 0 1 12 6.5A2.5 2.5 0 0 1 14.5 9a2.5 2.5 0 0 1-2.5 2.5M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7"/></svg>
                  <a
                    href="https://www.google.com/maps/place/Salamis+Hotel/@35.2057002,33.8964633,17z/data=!3m1!4b1!4m9!3m8!1s0x14dfb69704fb4acb:0x87cc7e1e86d6d3cf!5m2!4m1!1i2!8m2!3d35.2056958!4d33.8990436!16s%2Fg%2F1tgc1_gb?entry=ttu"
                    class="nav-link"
                    rel="nofollow noopener noreferrer"
                    target="_blank"
                    >{{ $t("address") }}
                  </a>
                </li>
                <li class="itm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.28-.28.67-.36 1.02-.25c1.12.37 2.32.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57c.11.35.03.74-.25 1.02z"/></svg>
                  <a href="tel:+903926330000" class="nav-link">+90 392 633 00 00</a>
                </li>
                <li class="itm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.28-.28.67-.36 1.02-.25c1.12.37 2.32.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57c.11.35.03.74-.25 1.02z"/></svg>
                  <a href="tel:+908507070101" class="nav-link">+90 850 707 01 01</a>
                  <span class="ft-phone-tag">{{ $t('phone_label_international') }}</span>
                </li>
                <li class="itm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M6.62 10.79c1.44 2.83 3.76 5.15 6.59 6.59l2.2-2.2c.28-.28.67-.36 1.02-.25c1.12.37 2.32.57 3.57.57a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1A17 17 0 0 1 3 4a1 1 0 0 1 1-1h3.5a1 1 0 0 1 1 1c0 1.25.2 2.45.57 3.57c.11.35.03.74-.25 1.02z"/></svg>
                  <a href="tel:08507070101" class="nav-link">0850 707 01 01</a>
                  <span class="ft-phone-tag">{{ $t('phone_label_kktc') }}</span>
                </li>
                <li class="itm">
                  <svg width="24" height="24" viewBox="0 0 50 50" fill="#25D366" xmlns="http://www.w3.org/2000/svg"><path d="M 25 2 C 12.309534 2 2 12.309534 2 25 C 2 29.079097 3.1186875 32.88588 4.984375 36.208984 L 2.0371094 46.730469 A 1.0001 1.0001 0 0 0 3.2402344 47.970703 L 14.210938 45.251953 C 17.434629 46.972929 21.092591 48 25 48 C 37.690466 48 48 37.690466 48 25 C 48 12.309534 37.690466 2 25 2 z M 25 4 C 36.609534 4 46 13.390466 46 25 C 46 36.609534 36.609534 46 25 46 C 21.278025 46 17.792121 45.029635 14.761719 43.333984 A 1.0001 1.0001 0 0 0 14.033203 43.236328 L 4.4257812 45.617188 L 7.0019531 36.425781 A 1.0001 1.0001 0 0 0 6.9023438 35.646484 C 5.0606869 32.523592 4 28.890107 4 25 C 4 13.390466 13.390466 4 25 4 z M 16.642578 13 C 16.001539 13 15.086045 13.23849 14.333984 14.048828 C 13.882268 14.535548 12 16.369511 12 19.59375 C 12 22.955271 14.331391 25.855848 14.613281 26.228516 L 14.615234 26.230469 C 14.973031 26.752191 15.486328 27.419922 15.999626 28.087653 C 16.717405 28.96464 17.619141 29.914062 19.422612 31.812909 C 21.958282 34.007419 25.105469 35.349609 26.554789 35.966779 C 27.698179 36.339417 28.564453 36.611328 30.169845 37.115426 C 31.632073 37.038799 32.730469 36.876953 33.55263 36.755876 C 34.456878 36.361114 35.351562 35.794922 36.246248 35.22873 C 37.12309 34.524722 37.509766 33.455078 37.786772 32.688244 C 37.927591 31.979598 37.978516 31.396484 37.988281 30.609375 C 37.969311 30.371148 37.767578 29.824219 37.302009 29.059804 C 36.774753 29.039853 36.224609 28.767578 35.337891 30.558594 C 35.748225 30.761674 35.942113 30.893881 35.992188 30.894531 C 35.998992 31.07786 35.986328 31.222656 35.951258 31.624292 C 35.8439 32.180225 35.628906 32.775391 35.523582 33.066746 C 34.975018 33.667661 34.283203 34.105469 33.591388 34.543277 C 32.749338 34.852514 32.4375 34.898438 31.499896 35.036591 C 30.386672 35.087027 29.164062 34.703125 28.316336 34.437036 C 27.259305 34.092596 25.890625 33.509766 23.114812 32.325956 C 20.755591 30.311513 19.070312 28.537109 18.227674 27.649908 C 17.552562 26.824019 17.072266 26.199219 16.592866 25.575584 C 16.383528 25.251054 16.208984 25.021484 15.897202 24.609805 C 14 21.970851 14 19.59375 14 19.59375 C 14 17.077989 15.168497 16.091436 15.800781 15.410156 C 16.132721 15.052495 16.495617 15 16.642578 15 z"/></svg>
                  <a href="https://api.whatsapp.com/send?phone=905469999333" class="nav-link" rel="nofollow noopener noreferrer" target="_blank">+90 546 999 93 33</a>
                  <span class="ft-phone-tag">{{ $t('phone_label_whatsapp') }}</span>
                </li>
                <li class="itm">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M19.07 13.88L13 19.94V22h2.06l6.06-6.07m1.58-2.35l-1.28-1.28a.52.52 0 0 0-.38-.17c-.15.01-.29.06-.39.17l-1 1l2.05 2l1-1c.19-.2.19-.52 0-.72M11 18H4V8l8 5l8-5v2h2V6c0-1.1-.9-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h7zm9-12l-8 5l-8-5z"/></svg>
                  <a href="mailto:onlinecc@salamisbayconti.com" class="nav-link"
                    >onlinecc@salamisbayconti.com
                  </a>
                </li>
              </ul>
            </div>
            <div class="row-line">
              <span class="row-line"></span>
            </div>
            <div class="row">
              <div class="col">
                <p>© {{ new Date().getFullYear() }} Salamis Bay Conti Resort Hotel ★★★★★</p>
              </div>
              <div class="col-auto">
                <NuxtLink :to="redirect('quality')" class="nav-link">
                  {{ $t("quality_title") }}
                </NuxtLink>
                <NuxtLink :to="redirect('policy')" class="nav-link">
                  {{ $t("privacy_title") }}
                </NuxtLink>
                <NuxtLink :to="redirect('refund')" class="nav-link">
                  {{ $t("refund_title") }}
                </NuxtLink>
                <NuxtLink to="https://salamisbayconti.com/sitemap.xml" target="_blank" rel="nofollow noopener noreferrer" aria-label="See sitemap" class="nav-link">
                  Sitemap
                </NuxtLink>
              </div>
            </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.footer-section {
  /* Pure-CSS fade-in: no JS/hydration dependency, so the footer can never
     get stuck invisible if a client-side mount is slow, delayed, or fails
     (as happened intermittently on mobile with the old isReady-gated
     opacity, set from a mounted() timeout — a flaky connection could leave
     the async Footer chunk unhydrated, isReady stuck false, and the footer
     permanently opacity:0/visibility:hidden until a manual refresh).
     No content-visibility:auto: without an intrinsic size, mobile Safari
     could leave the footer unpainted on first load. */
  background-color: #042333;
  animation: footer-fade-in 0.3s ease;
}
@keyframes footer-fade-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
.row-line {
  width: 100%;
  background-color: white;
  height: 1px;
  color: white;
  margin-block: 18px;
}
.footer {
  padding-top: 4%;
  contain: layout style;
}
.logo {
  max-width: 130px;
 
  object-fit: contain;
  margin-top: -30px;
  padding-top: 0;
}
.nav-item {
  margin-block: 4px;
}
.nav-link:hover {
  text-decoration: underline 2px solid;
}
.app-cta {
  font-size: 1rem;
  white-space: nowrap;
  margin-bottom: 4px;
}
.qr {
  width: 100px;
  aspect-ratio: 1 / 1;
  object-fit: contain;
}
.app-review-row {
  margin-top: 6px;
}
.icon {
  padding: 0 10px 0 0;
  width: 140px;
  aspect-ratio: 3.4 / 1;
  object-fit: contain;
}
.badge-frame, .qr-frame {
  position: relative;
  display: inline-block;
  line-height: 0;
  max-width: 100%;
}
.qr-frame {
  border-radius: 8px;
  overflow: hidden;
}
/* Images paint over the skeleton as soon as the browser loads them, so they
   never wait on JS hydration (the skeleton is only removed by the @load handler). */
.icon, .qr {
  position: relative;
  z-index: 1;
}
.footer-skel {
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, .08);
  border-radius: inherit;
}
.footer-skel::after {
  content: "";
  position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, .14), transparent);
  transform: translateX(-100%);
  animation: footer-skel-shimmer 1.5s ease-in-out infinite;
}
@keyframes footer-skel-shimmer {
  100% { transform: translateX(100%); }
}
.itm{
  margin-left: -5px;
  font-size: 0.82rem;
}
.itm .nav-link {
  font-size: 1rem;
}
.ft-phone-tag {
  font-size: 0.55rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.55;
}
.ft-phone-tag::before { content: '('; }
.ft-phone-tag::after  { content: ')'; }
@media screen and (max-width: 960px) {
  .footer {
    padding: 20px !important;
    margin: 0 !important;
  }
  .logo {
  width: 200px;
 
  object-fit: contain;
  margin-top: 0px;
  padding-top: 0;
}
  .app-cta {
    white-space: normal;
  }
  .qr {
    width: 90px;
    height: 90px;
    aspect-ratio: 1 / 1;
    object-fit: contain;
  }
}
</style>
